import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { BusinessUnitSummary } from '@models/business-unit.interface';
import { RigSummary } from '@models/rig.interface';
import { WellSummary } from '@models/well.interface';
import { BusinessUnitsService } from '@services/business-units.service';
import { JobsService, JobRequestSearchResult, JobRequestSearchQuery } from '@services/jobs.service';
import { RigsService } from '@services/rigs.service';
import { WellsService } from '@services/wells.service';
import { SearchParameters } from '@shared/models/search-parameters.model';

interface TestTypeOption {
  id: number;
  name: string;
  alias: string | null;
}

@Component({
  selector: 'app-job-request-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatPaginatorModule, MatProgressSpinnerModule],
  templateUrl: './job-request-search.component.html',
  styleUrl: './job-request-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobRequestSearchComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly businessUnitsService = inject(BusinessUnitsService);
  private readonly jobsService = inject(JobsService);
  private readonly rigsService = inject(RigsService);
  private readonly wellsService = inject(WellsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  protected readonly requestTypes = ['Mud Check', 'Cutback', 'Weigh Up', 'Pilot'];
  protected readonly searchForm = this.formBuilder.group({
    wellId: [null as number | null],
    rigId: [null as number | null],
    businessUnitId: [null as number | null],
    requestType: [null as string | null],
    testTypeIds: [[] as number[]],
    startDateFrom: [null as string | null],
    startDateTo: [null as string | null],
    densityFrom: [null as number | null, [Validators.min(0)]],
    densityTo: [null as number | null, [Validators.min(0)]],
    material: [''],
  });

  protected wells: WellSummary[] = [];
  protected rigs: RigSummary[] = [];
  protected businessUnits: BusinessUnitSummary[] = [];
  protected testTypes: TestTypeOption[] = [];
  protected results: JobRequestSearchResult[] = [];
  protected totalCount = 0;
  protected pageIndex = 0;
  protected pageSize = 25;
  protected isLoadingLookups = true;
  protected isSearching = false;
  protected hasSearched = false;
  protected errorMessage = '';

  ngOnInit(): void {
    forkJoin({
      wells: this.wellsService.getList(),
      rigs: this.rigsService.getList(),
      businessUnits: this.businessUnitsService.getList(),
      testTypes: this.jobsService.getTestTypesForMudCheck(),
    }).subscribe({
      next: ({ wells, rigs, businessUnits, testTypes }) => {
        this.wells = wells;
        this.rigs = rigs;
        this.businessUnits = businessUnits;
        this.testTypes = testTypes
          .map(test => ({
            id: Number(test.id),
            name: test.name || test.alias || `Test ${test.id}`,
            alias: test.alias || null,
          }))
          .filter((test, index, all) => all.findIndex(item => item.id === test.id) === index)
          .sort((left, right) => left.name.localeCompare(right.name));
        this.isLoadingLookups = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Unable to load search lists.';
        this.isLoadingLookups = false;
        this.cdr.markForCheck();
      },
    });
  }

  protected search(): void {
    const values = this.searchForm.getRawValue();
    if (values.startDateFrom && values.startDateTo && values.startDateFrom > values.startDateTo) {
      this.errorMessage = 'The start date range is invalid.';
      return;
    }
    if (values.densityFrom !== null && values.densityTo !== null && values.densityFrom > values.densityTo) {
      this.errorMessage = 'The density range is invalid.';
      return;
    }

    this.errorMessage = '';
    this.pageIndex = 0;
    this.loadResults();
  }

  protected clear(): void {
    this.searchForm.reset({
      wellId: null,
      rigId: null,
      businessUnitId: null,
      requestType: null,
      testTypeIds: [],
      startDateFrom: null,
      startDateTo: null,
      densityFrom: null,
      densityTo: null,
      material: '',
    });
    this.results = [];
    this.totalCount = 0;
    this.hasSearched = false;
    this.errorMessage = '';
  }

  protected onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadResults();
  }

  protected getJobRequestUrl(result: JobRequestSearchResult): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['/jobs', result.jobId, 'requests', result.jobRequestId])
    );
  }

  private loadResults(): void {
    const values = this.searchForm.getRawValue();
    const query: JobRequestSearchQuery = {
      wellId: values.wellId,
      rigId: values.rigId,
      businessUnitId: values.businessUnitId,
      requestType: values.requestType,
      testTypeIds: values.testTypeIds?.length ? values.testTypeIds.map(Number) : null,
      startDateFrom: values.startDateFrom,
      startDateTo: values.startDateTo,
      densityFrom: values.densityFrom,
      densityTo: values.densityTo,
      material: values.material?.trim() || null,
    };
    const searchParameters: SearchParameters<JobRequestSearchQuery> = {
      query,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    };

    this.isSearching = true;
    this.hasSearched = true;
    this.jobsService.searchJobRequests(searchParameters).subscribe({
      next: result => {
        this.results = result.items;
        this.totalCount = result.totalCount;
        this.pageIndex = result.pageIndex;
        this.pageSize = result.pageSize;
        this.isSearching = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.results = [];
        this.totalCount = 0;
        this.isSearching = false;
        this.errorMessage = 'The job request search failed.';
        this.cdr.markForCheck();
      },
    });
  }
}